import { useMemo } from "react";
import { Link } from "react-router-dom";
import { posts } from "@/mocks/posts";
import { categories } from "@/mocks/categories";
import PageHero from "@/components/feature/PageHero";
import JsonLd from "@/components/feature/JsonLd";
import { formatViews } from "@/components/feature/PostCard";
import usePageMeta from "@/hooks/usePageMeta";

function formatDay(dateStr: string) {
  const date = new Date(`${dateStr}T00:00:00`);
  const week = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
  return {
    md: `${String(date.getMonth() + 1).padStart(2, "0")} / ${String(date.getDate()).padStart(2, "0")}`,
    week: week[date.getDay()],
    full: `${date.getFullYear()} 年 ${date.getMonth() + 1} 月 ${date.getDate()} 日`,
  };
}

export default function Archive() {
  usePageMeta({
    title: "归档中心 - 按日期回溯全部更新 | 黑料每日",
    description:
      "黑料每日归档中心按日期陈列全部更新记录，包含每日新增内容与历史帖子，方便长期蹲守的读者顺时间线回溯，全量更新不漏任何一条。",
    canonical: "https://ranqiwangyou.cn/archive",
  });

  const groups = useMemo(() => {
    const map = new Map<string, typeof posts>();
    [...posts]
      .sort((a, b) => b.date.localeCompare(a.date) || b.views - a.views)
      .forEach((post) => {
        if (!map.has(post.date)) map.set(post.date, []);
        map.get(post.date)!.push(post);
      });
    return Array.from(map.entries());
  }, []);

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "归档中心 · 按日期回溯全部更新",
          description: "按日期陈列每日更新记录，方便回溯历史内容，全量更新不漏任何一条。",
          inLanguage: "zh-CN",
          url: "https://ranqiwangyou.cn/archive",
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://ranqiwangyou.cn/" },
            { "@type": "ListItem", position: 2, name: "归档中心" },
          ],
        }}
      />

      <PageHero
        eyebrow="归档中心"
        title="按日期回溯 · 全量更新不漏一条"
        desc="这里是所有更新记录的完整时间线。每一天的内容独立归档，长期蹲守的读者可以顺着日期一路翻回去。"
        crumbs={[{ label: "归档中心" }]}
        meta={
          <div className="rounded-lg border border-background-300 bg-background-50 px-5 py-3 text-center">
            <p className="font-heading text-2xl font-black text-foreground-950">
              {posts.length}
            </p>
            <p className="text-xs text-foreground-500">累计归档条目</p>
          </div>
        }
      />

      <section className="w-full px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-32">
              <h2 className="mb-3 text-sm font-semibold tracking-wide text-foreground-500">
                日期速览
              </h2>
              <ul className="flex flex-wrap gap-2 lg:flex-col">
                {groups.map(([date, dayPosts]) => {
                  const info = formatDay(date);
                  return (
                    <li key={date}>
                      <a
                        href={`#day-${date}`}
                        className="flex cursor-pointer items-center justify-between gap-3 rounded-md bg-background-100 px-3.5 py-2.5 text-sm transition-colors hover:bg-background-200"
                      >
                        <span className="font-medium text-foreground-900">{info.md}</span>
                        <span className="text-xs text-foreground-500">{dayPosts.length} 条</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-9">
            <div className="relative border-l border-background-300 pl-6 md:pl-8">
              {groups.map(([date, dayPosts]) => {
                const info = formatDay(date);
                return (
                  <div key={date} id={`day-${date}`} className="relative pb-10 last:pb-0">
                    <span className="absolute -left-[31px] top-1 flex h-4 w-4 items-center justify-center rounded-full border-2 border-background-50 bg-primary-500 md:-left-[39px]"></span>
                    <div className="mb-4 flex flex-wrap items-baseline gap-3">
                      <h2 className="font-heading text-xl font-black text-foreground-950">
                        {info.full}
                      </h2>
                      <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-800">
                        {info.week}
                      </span>
                      <span className="text-xs text-foreground-500">
                        当日更新 {dayPosts.length} 条
                      </span>
                    </div>

                    <div className="space-y-3">
                      {dayPosts.map((post) => {
                        const cat = categories.find((c) => c.id === post.categoryId);
                        return (
                          <Link
                            key={post.id}
                            to={`/post/${post.id}`}
                            className="group flex cursor-pointer items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                          >
                            <div className="relative h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-200">
                              <img
                                src={post.cover}
                                alt={`${post.title}归档配图`}
                                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-xs text-secondary-800">
                                  {cat?.name}
                                </span>
                                {post.hot && (
                                  <span className="flex items-center gap-1 rounded bg-primary-100 px-1.5 py-0.5 text-xs text-primary-700">
                                    <i className="ri-fire-line"></i>
                                    热议
                                  </span>
                                )}
                              </div>
                              <h3 className="mt-1.5 line-clamp-2 font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                                {post.title}
                              </h3>
                              <div className="mt-1.5 flex items-center gap-3 text-xs text-foreground-500">
                                <span className="flex items-center gap-1">
                                  <i className="ri-eye-line"></i>
                                  {formatViews(post.views)}
                                </span>
                                <span className="hidden sm:inline">
                                  #{post.tags[0]}
                                </span>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}